import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Search, Plus, Pencil, Trash2, AlertCircle, RefreshCw } from "lucide-react";
import { useDoctorsApi } from "../../hooks/useDoctorsApi";
import { useAdminApi } from "../../hooks/useAdminApi";
import UnifiedDoctorForm from "./UnifiedDoctorForm";

const AdminDoctors = () => {
  const { getDoctors, loading: fetchLoading } = useDoctorsApi();
  const { deleteDoctor } = useAdminApi();

  const [doctors, setDoctors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const fetchedRef = useRef(false);

  const loadDoctors = useCallback(async () => {
    fetchedRef.current = false;
    setFetchError(null);
    try {
      const response = await getDoctors();
      if (response?.data?.doctors) {
        setDoctors(response.data.doctors);
      }
    } catch (err) {
      setFetchError("Failed to load doctors.");
    } finally {
      fetchedRef.current = true;
    }
  }, [getDoctors]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  const filteredDoctors = doctors.filter((d) => {
    const q = searchQuery.toLowerCase();
    return (
      d.name?.toLowerCase().includes(q) ||
      d.specialty?.toLowerCase().includes(q) ||
      d.clinic_name?.toLowerCase().includes(q) ||
      d.city?.toLowerCase().includes(q)
    );
  });

  const handleAddClick = () => {
    setEditingDoctor(null);
    setFormOpen(true);
  };

  const handleEditClick = (doctor) => {
    setEditingDoctor(doctor);
    setFormOpen(true);
  };

  const handleFormSave = (savedDoctor) => {
    setFormOpen(false);
    if (editingDoctor) {
      setDoctors((prev) =>
        prev.map((d) => (d.id === savedDoctor.id ? savedDoctor : d))
      );
    } else {
      setDoctors((prev) => [savedDoctor, ...prev]);
    }
    setEditingDoctor(null);
  };

  const handleFormCancel = () => {
    setFormOpen(false);
    setEditingDoctor(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteDoctor(deleteId);
      setDoctors((prev) => prev.filter((d) => d.id !== deleteId));
    } catch {
      // useApi showToast:true handles error toast
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by name, specialty, clinic, city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          onClick={handleAddClick}
          className="bg-orange-600 hover:bg-orange-700 shrink-0"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Doctor
        </Button>
      </div>

      {/* Error state */}
      {fetchError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{fetchError}</span>
            </div>
            <Button variant="outline" size="sm" onClick={loadDoctors}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Doctors table */}
      <Card>
        <CardContent className="p-0">
          {fetchLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse h-10 bg-gray-100 rounded" />
              ))}
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              {searchQuery ? "No doctors match your search." : "No doctors found."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Specialty</TableHead>
                    <TableHead>Clinic</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDoctors.map((doctor) => (
                    <TableRow key={doctor.id}>
                      <TableCell className="font-medium">{doctor.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-orange-200 text-orange-700">
                          {doctor.specialty || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {doctor.clinic_name || doctor.occupation || "—"}
                      </TableCell>
                      <TableCell className="text-gray-600">{doctor.city || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditClick(doctor)}
                            className="border-orange-200 text-orange-600 hover:bg-orange-50"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteId(doctor.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unified Doctor Form */}
      <UnifiedDoctorForm
        open={formOpen}
        doctor={editingDoctor}
        onSave={handleFormSave}
        onCancel={handleFormCancel}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Doctor</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete this doctor? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setDeleteId(null)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDoctors;
